'use client';

import { useQuery } from '@apollo/client';
import { Button } from '@repo/design-system/components/ui/button';
import { Skeleton } from '@repo/design-system/components/ui/skeleton';
import { Toaster } from '@repo/design-system/components/ui/toaster';
import { useToast } from '@repo/design-system/hooks/use-toast';
import { useParams, usePathname, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { contentConfig } from '../config/content-config';
import { formConfig } from '../config/form-config';

import { AdditionalInsureds } from '../components/AdditionalInsureds';
import { CertificateRequirements } from '../components/CertificateRequirements';
import { CompanyHeader } from '../components/CompanyHeader';
import { Footer } from '../components/Footer';
import { InsuranceRequirements } from '../components/InsuranceRequirements';
import { UploadSection } from '../components/UploadSection';

import { Form } from '@repo/design-system/components/ui/form';
import { useForm } from 'react-hook-form';
import { handleGQLError } from '../../lib/errors';
import { FormFields } from '../components/FormFields';
import {
  COI_UPLOADER_SESSION_QUERY,
  TRACKER_COI_UPLOADER_SESSION_QUERY,
} from '../queries';
import { transformCoiData } from '../utils/transform-coi-data';

interface ContractorData {
  first_name: string;
  last_name: string;
  company_name: string;
  email: string;
}

export default function InsuranceApplicationPage() {
  const router = useRouter();
  const { toast } = useToast();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const params = useParams();
  const pathname = usePathname();
  const trackerId = params.iaSession as string;
  const isSandbox = pathname.startsWith('/sandbox/');

  const isTrackerSession = trackerId?.startsWith('trs_');

  const { data, loading, error } = useQuery(
    isTrackerSession
      ? TRACKER_COI_UPLOADER_SESSION_QUERY
      : COI_UPLOADER_SESSION_QUERY,
    {
      variables: {
        coiUploadSession: trackerId,
      },
      skip: !trackerId,
    }
  );

  const form = useForm({
    defaultValues: {
      firstname: '',
      lastname: '',
      businessname: '',
      email: '',
    },
  });

  // Parse contractor data and update form when data is loaded
  useEffect(() => {
    if (data?.coiUploadSession?.contractor?.unredactedDict) {
      try {
        const contractorData: ContractorData = JSON.parse(
          data.coiUploadSession.contractor.unredactedDict
        );
        form.reset({
          firstname: contractorData.first_name || '',
          lastname: contractorData.last_name || '',
          businessname: contractorData.company_name || '',
          email: contractorData.email || '',
        });
      } catch (err) {
        console.error('Error parsing contractor data:', err);
      }
    }
  }, [data, form]);

  interface FormData {
    firstname: string;
    lastname: string;
    businessname?: string;
    email: string;
  }

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [recordCount, setRecordCount] = useState<number | null>(null);

  // Extract contractor publicId from the loaded data
  const contractorPublicId =
    data?.coiUploadSession?.contractor?.publicId ||
    data?.trackerCoiUploadSession?.contractor?.publicId ||
    '';

  const onSubmit = async (data: FormData) => {
    setSubmitError(null);
    if (uploadedFiles.length === 0) {
      setSubmitError('Please upload at least one certificate PDF file.');
      toast({
        title: 'Upload Error',
        description: 'Please upload at least one certificate PDF file.',
        variant: 'destructive',
      });
      return;
    }

    if (isTrackerSession && !data.businessname) {
      setSubmitError('Business name is required.');
      toast({
        title: 'Missing required field',
        description: 'Business name is required.',
        variant: 'destructive',
      });
      return;
    }
    try {
      const formData = new FormData();
      const apiHost = process.env.NEXT_PUBLIC_API_BACKEND_URL || '';
      let apiUrl: string;

      if (isTrackerSession) {
        // Use tracker bulk endpoint for tracker sessions
        formData.append('tracker_id', trackerId);

        // Create certificates metadata for tracker bulk API
        const certificatesMetadata = uploadedFiles.map((file) => ({
          filename: file.name,
          contractor: {
            email: data.email,
            first_name: data.firstname,
            last_name: data.lastname,
            company_name: data.businessname || '',
          },
        }));

        formData.append(
          'certificates_metadata',
          JSON.stringify(certificatesMetadata)
        );

        // Append all certificate files
        for (const file of uploadedFiles) {
          formData.append('certificates', file);
        }

        apiUrl = `${apiHost}/files/external_certificates/tracker/bulk`;
      } else {
        // Use bulk endpoint for regular COI sessions
        formData.append('first_name', data.firstname);
        formData.append('last_name', data.lastname);
        formData.append('company_name', data.businessname || '');
        formData.append('email', data.email);
        formData.append('coi_upload_session_id', trackerId);
        formData.append('contractor_id', contractorPublicId);

        // Append all certificate files
        for (const file of uploadedFiles) {
          formData.append('certificates', file);
        }

        apiUrl = `${apiHost}/files/external_certificates/upload/bulk`;
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Ten99Policy-Environment': isSandbox ? 'sandbox' : (process.env.NEXT_PUBLIC_ENVIRONMENT || 'production'),
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        setSubmitError(errorData.message || 'Failed to upload certificates.');
        toast({
          title: 'Upload Error',
          description: errorData.message || 'Failed to upload certificates.',
          variant: 'destructive',
        });
        return;
      }
      const fileCount = uploadedFiles.length;
      toast({
        title: 'Success',
        description: `${fileCount} certificate${fileCount !== 1 ? 's' : ''} uploaded successfully!`,
        variant: 'default',
      });
      // Navigate to thank you page
      const basePath = isSandbox ? '/sandbox' : '';
      router.push(`${basePath}/${trackerId}/thank-you`);
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : 'An error occurred during submission.';
      setSubmitError(message);
      toast({
        title: 'Upload Error',
        description: message,
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="h-screen">
        <div className="grid h-full grid-cols-1 md:grid-cols-2">
          {/* Form column skeleton */}
          <div className="order-1 flex h-full flex-col overflow-y-auto border-l bg-muted/50 p-6 shadow-[-1px_0_0_0_rgba(0,0,0,0.03)] md:order-2 md:p-12">
            <div className="mx-auto flex w-full max-w-xl flex-1 flex-col">
              <div className="mb-4">
                <Skeleton className="mb-2 h-6 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
              <div className="space-y-4">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            </div>
          </div>

          {/* Details column skeleton */}
          <div className="order-2 hidden h-full w-full flex-col overflow-y-auto px-8 py-6 md:order-1 md:flex md:p-12">
            <div className="w-full flex-1 space-y-6">
              <div className="space-y-4">
                <Skeleton className="h-8 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
              <div className="space-y-4">
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    const errorResult = handleGQLError(error, { router, trackerId, isSandbox });

    if (!errorResult) {
      return null; // This handles the PAGE_EXPIRED case where we redirect
    }

    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h2 className="mb-2 font-semibold text-2xl">{errorResult.title}</h2>
          <p className="text-muted-foreground">{errorResult.message}</p>
        </div>
      </div>
    );
  }

  const transformedData =
    data?.trackerCoiUploadSession || data?.coiUploadSession
      ? transformCoiData(data.trackerCoiUploadSession || data.coiUploadSession)
      : {
          company: {
            name: contentConfig.company.name,
            subtitle: contentConfig.company.subtitle,
          },
          header: {
            title: contentConfig.header.title,
            description: contentConfig.header.description,
          },
          insurance: contentConfig.insurance,
          additionalInsureds: contentConfig.additionalInsureds,
          certificateRequirements: contentConfig.certificateRequirements,
        };

  return (
    <>
      <div className="h-screen">
        <div className="h-full">
          <div className="grid h-full grid-cols-1 md:grid-cols-2">
            {/* Form column (first on mobile) */}
            <div className="order-1 flex h-full flex-col overflow-y-auto border-l bg-muted/50 p-6 shadow-[-1px_0_0_0_rgba(0,0,0,0.03)] md:order-2 md:border-l md:p-12">
              <div className="mx-auto flex w-full max-w-xl flex-1 flex-col">
                {/* Header (fixed at top on mobile) */}
                <div className="mb-4 flex items-center justify-between">
                  <div className="min-w-0">
                    <h3 className="mb-0.5 font-medium text-base">
                      {transformedData.header.title}
                    </h3>
                    <p className="text-base text-muted-foreground">
                      {transformedData.header.description}
                    </p>
                  </div>
                  {/* Mobile: Show details toggle button */}
                  <div className="ml-4 flex-shrink-0 md:hidden">
                    <Button
                      type="button"
                      variant="outline"
                      className="whitespace-nowrap"
                      onClick={() => setDetailsOpen((open) => !open)}
                    >
                      {detailsOpen ? 'Hide Details' : 'Show Details'}
                    </Button>
                  </div>
                </div>

                {/* Mobile: Requirements below header when expanded */}
                {detailsOpen && (
                  <div className="mb-6 md:hidden">
                    <div className="w-full space-y-6">
                      <InsuranceRequirements
                        insurance={transformedData.insurance}
                      />
                      <AdditionalInsureds
                        insureds={transformedData.additionalInsureds}
                        spacing="wide"
                      />
                    </div>
                  </div>
                )}

                {/* Form fields, upload, and submit button */}
                <div className="overflow-y-auto border-foreground/[0.03]">
                  <div className="mx-auto">
                    <Form {...form}>
                      <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-2"
                      >
                        <FormFields
                          sections={formConfig.sections}
                          className="space-y-6 px-1"
                        />
                        <UploadSection
                          upload={formConfig.upload}
                          handleBulkFileSelect={(files) => {
                            setUploadedFiles(files || []);
                            setRecordCount(files ? files.length : null);
                          }}
                          handleBulkUpload={(files: File[]) => {
                            return Promise.resolve({
                              recordCount: files.length,
                              filenames: files.map((f) => f.name),
                            });
                          }}
                          handleBulkUploadComplete={() => {
                            // No additional action needed after upload
                          }}
                          recordCount={recordCount ?? 0}
                          setRecordCount={(count) =>
                            setRecordCount(
                              typeof count === 'number' ? count : 0
                            )
                          }
                          handleReset={() => {
                            setUploadedFiles([]);
                            setRecordCount(null);
                          }}
                          error={null}
                          maxFiles={20}
                          maxSizeMB={100}
                        />
                        <Button
                          type="submit"
                          className="w-full rounded bg-blue-600 text-white hover:bg-blue-700"
                        >
                          Submit
                        </Button>
                      </form>
                    </Form>
                  </div>
                </div>

                {/* Footer for Mobile */}
                <div className="mt-auto w-full pt-6 text-center md:hidden">
                  <Footer footer={contentConfig.footer} />
                </div>
              </div>
            </div>

            {/* Details column (desktop only) */}
            <div className="order-2 mx-auto hidden h-full w-full max-w-2xl flex-col overflow-y-auto px-8 py-6 md:order-1 md:flex md:p-12">
              <div className="w-full flex-1 space-y-6">
                <CompanyHeader company={transformedData.company} />
                <InsuranceRequirements insurance={transformedData.insurance} />
                <CertificateRequirements
                  requirements={transformedData.certificateRequirements}
                />
                <AdditionalInsureds
                  insureds={transformedData.additionalInsureds}
                  spacing="wide"
                />
              </div>
              {/* Footer for Desktop */}
              <div className="mt-auto w-full pt-6 text-center">
                <Footer footer={contentConfig.footer} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Toaster />
    </>
  );
}
